import { useEffect, useRef, useState } from "react";
import { useI18n } from "@/i18n/I18nProvider";

function scrollToSection(id) {
    if (id === "top") { window.scrollTo({ top: 0, behavior: "smooth" }); return; }
    const el = document.getElementById(id);
    if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
}

function useActiveSection(items) {
    const [active, setActive] = useState(items[0].id);
    useEffect(() => {
        const nodes = items.map((i) => document.getElementById(i.id)).filter(Boolean);
        if (!nodes.length) return undefined;
        const io = new IntersectionObserver(
            (entries) => {
                for (const e of entries) if (e.isIntersecting) setActive(e.target.id);
            },
            { rootMargin: "-40% 0px -50% 0px", threshold: 0 },
        );
        nodes.forEach((n) => io.observe(n));
        return () => io.disconnect();
    }, [items]);
    return active;
}

function MobileMenu({ items, active, t, onPick }) {
    return (
        <div role="menu" data-testid="section-nav-menu" className="itly-sectionnav-menu">
            {items.map((it) => (
                <button
                    key={it.id}
                    type="button"
                    role="menuitem"
                    data-testid={`section-nav-item-${it.id}`}
                    aria-current={active === it.id ? "true" : undefined}
                    className={`itly-sectionnav-menu-item${active === it.id ? " is-active" : ""}`}
                    onClick={() => onPick(it.id)}
                >
                    <span className="itly-sectionnav-menu-dot" />
                    {t(it.labelKey)}
                </button>
            ))}
        </div>
    );
}

export default function SectionNav({ items }) {
    const { t } = useI18n();
    const active = useActiveSection(items);
    const [open, setOpen] = useState(false);
    const wrapRef = useRef(null);

    useEffect(() => {
        if (!open) return undefined;
        const close = (e) => { if (wrapRef.current && !wrapRef.current.contains(e.target)) setOpen(false); };
        document.addEventListener("pointerdown", close);
        return () => document.removeEventListener("pointerdown", close);
    }, [open]);

    const pick = (id) => { setOpen(false); scrollToSection(id); };
    const activeItem = items.find((i) => i.id === active) || items[0];

    return (
        <>
            <nav aria-label={t("sectionnav.aria")} data-testid="section-nav-desktop" className="itly-sectionnav-desktop">
                {items.map((it) => (
                    <button
                        key={it.id}
                        type="button"
                        data-testid={`section-dot-${it.id}`}
                        aria-label={t(it.labelKey)}
                        aria-current={active === it.id ? "true" : undefined}
                        className={`itly-sectionnav-dot${active === it.id ? " is-active" : ""}`}
                        onClick={() => scrollToSection(it.id)}
                    >
                        <span className="itly-sectionnav-label">{t(it.labelKey)}</span>
                        <span className="itly-sectionnav-dot-mark" />
                    </button>
                ))}
            </nav>

            <div ref={wrapRef} data-testid="section-nav-mobile" className="itly-sectionnav-mobile">
                {open && <MobileMenu items={items} active={active} t={t} onPick={pick} />}
                <button
                    type="button"
                    data-testid="section-nav-toggle"
                    aria-haspopup="menu"
                    aria-expanded={open}
                    className="itly-sectionnav-pill"
                    onClick={() => setOpen((v) => !v)}
                >
                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" aria-hidden="true">
                        <path d="M4 6h16M4 12h16M4 18h10" />
                    </svg>
                    <span className="itly-sectionnav-pill-label">{t(activeItem.labelKey)}</span>
                    <span aria-hidden="true" style={{ fontSize: 9, opacity: 0.8 }}>{open ? "▾" : "▴"}</span>
                </button>
            </div>
        </>
    );
}
